import Dashboard from '../../Dashboard';
import MenuContainer from '../../custom-components/menu/MenuContainer';
import { useNavigate } from 'react-router-dom';
import AdaptiveContainer from '../../containers/AdaptiveContainer';
import { Box, Stack, useToast } from '@chakra-ui/react';
import React, { useState } from 'react';
import InputNames from '../../custom-components/text/InputNames';
import Input from '../../custom-components/Inputs/Input';
import ZaraSelect from '../../custom-components/select/ZaraSelect';
import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import { ChangeProfile } from '../../../graphql/mutations/ChangeProfile';
import { useCloseEvent } from '../../../hooks';
import ZaraButton from '../../custom-components/buttons/Button';
import { GetMe } from '../../../graphql/queries/GetMe';
import { useError } from '../../../error-provider/ErrorProvider';
import { PATHS } from '../../../router/routes';
import { z } from 'zod';
import FormInput from '../../custom-components/Inputs/FormInput';
import { companyNameSchema, languageSchema, nameSchema, phoneSchema, websiteSchema } from '../../../utils/schemas';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { INPUT_PROPS } from '../../../utils/constants';

const digitPattern = /^[\d+]*$/;

const schema = z
    .object({
        firstName: nameSchema,
        lastName: nameSchema,
        companyName: companyNameSchema,
        phoneNumber: phoneSchema,
        website: websiteSchema,
        language: languageSchema,
    })
    .refine((data) => digitPattern.test(data.phoneNumber), {
        message: 'Invalid number',
        path: ['phoneNumber'],
    });

const MobileChangeInfo = () => {
    const { showError } = useError();
    const router = useNavigate();

    const languages = [{ name: 'English', value: 1 }];

    const apolloClient = useApolloClient();

    const toast = useToast();

    const { data: userMe } = useQuery(GetMe, {
        onError(error) {
            showError(undefined, error);
        },
    });

    const userEmail = userMe?.getMe?.email;
    const language = userMe?.getMe?.language;
    const phoneNumber = userMe?.getMe?.phoneNumber;
    const companyName = userMe?.getMe?.company.name;
    const website = userMe?.getMe?.website;
    const firstName = userMe?.getMe?.firstName;
    const lastName = userMe?.getMe?.lastName;

    const { control, handleSubmit } = useForm({
        mode: 'onSubmit',
        resolver: zodResolver(schema),
        defaultValues: {
            firstName: firstName ?? '',
            lastName: lastName ?? '',
            language: language,
            phoneNumber: phoneNumber ?? '',
            companyName: companyName ?? '',
            website: website ?? '',
        },
    });

    const [isLangSelectFocus, setIsLangSelectFocus] = useState(false);

    useCloseEvent(setIsLangSelectFocus);

    const [onChangeProfile, { loading }] = useMutation(ChangeProfile, {
        onError(error) {
            showError(undefined, error);
        },
    });

    async function onSubmit(data) {
        const variables = {
            firstName: data.firstName,
            lastName: data.lastName,
            phoneNumber: data.phoneNumber,
            companyName: data.companyName,
            website: data.website,
            language: Number(data.language),
        };

        await onChangeProfile({
            variables: variables,
            async onCompleted(data) {
                if (data.changeProfile.__typename === 'ChangeProfileResponse') {
                    toast({
                        title: data.changeProfile.message,
                        status: 'success',
                        isClosable: true,
                    });

                    await apolloClient.writeQuery({
                        query: GetMe,
                        data: {
                            getMe: {
                                ...userMe.getMe,
                                firstName: variables.firstName,
                                lastName: variables.lastName,
                                website: variables.website,
                                phoneNumber: variables.phoneNumber,
                                language: variables.language,
                                company: {
                                    ...userMe.getMe.company,
                                    name: variables.companyName,
                                },
                            },
                        },
                    });
                }
            },
        });
    }

    const inputs = [
        {
            label: INPUT_PROPS.FIRST_NAME.LABEL,
            controllName: INPUT_PROPS.FIRST_NAME.CONTROLL_NAME,
            placeholder: INPUT_PROPS.FIRST_NAME.PLACEHOLDER,
        },
        {
            label: INPUT_PROPS.LAST_NAME.LABEL,
            controllName: INPUT_PROPS.LAST_NAME.CONTROLL_NAME,
            placeholder: INPUT_PROPS.LAST_NAME.PLACEHOLDER,
        },
        { value: userEmail, isDisabled: true },
        {
            label: INPUT_PROPS.PHONE_NUMBER.LABEL,
            controllName: INPUT_PROPS.PHONE_NUMBER.CONTROLL_NAME,
            placeholder: INPUT_PROPS.PHONE_NUMBER.PLACEHOLDER,
        },
        {
            label: INPUT_PROPS.COMPANY.LABEL,
            controllName: INPUT_PROPS.COMPANY.CONTROLL_NAME,
            placeholder: INPUT_PROPS.COMPANY.PLACEHOLDER,
        },
        {
            label: INPUT_PROPS.WEBSITE.LABEL,
            controllName: INPUT_PROPS.WEBSITE.CONTROLL_NAME,
            placeholder: INPUT_PROPS.WEBSITE.PLACEHOLDER,
        },
    ];

    return (
        <Dashboard title='dashboard'>
            <MenuContainer>
                <AdaptiveContainer w='90%' title='dashboard'>
                    <Box
                        css={{
                            '@media only screen and (max-width: 450px)': {
                                marginTop: '80px',
                            },
                            '@media only screen and (max-height: 450px)': {
                                marginTop: '110px',
                            },
                            '@media only screen and (min-width: 920px)': {
                                marginLeft: '100px',
                            },
                        }}
                        mb='100px'
                    >
                        <Box as='form' onSubmit={handleSubmit(onSubmit)}>
                            <Stack spacing='3'>
                                {inputs.map((item) => (
                                    <Box key={item}>
                                        {item.isDisabled ? (
                                            <Box mb='10px'>
                                                <InputNames color='#2D3748'>Email</InputNames>
                                                <Input value={item.value} isDisabled={item.isDisabled} />
                                            </Box>
                                        ) : (
                                            <FormInput
                                                control={control}
                                                label={item.label}
                                                controllName={item.controllName}
                                                placeholder={item.placeholder}
                                            />
                                        )}
                                    </Box>
                                ))}

                                <Box mb='10px'>
                                    <InputNames color='#2D3748'>Language</InputNames>
                                    <Controller
                                        control={control}
                                        name='language'
                                        render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
                                            <ZaraSelect
                                                isSelectFocus={isLangSelectFocus}
                                                setIsSelectFocus={setIsLangSelectFocus}
                                                selectValue={value}
                                                onBlur={onBlur}
                                                setSelectValue={onChange}
                                                placeholder='Choose language'
                                            >
                                                {languages.map((language) => (
                                                    <option
                                                        key={language.value}
                                                        disabled={language.value === value}
                                                        value={language.value}
                                                    >
                                                        {language.name}
                                                    </option>
                                                ))}
                                            </ZaraSelect>
                                        )}
                                    />
                                </Box>
                                <Box>
                                    <ZaraButton
                                        mb='20px'
                                        w='100%'
                                        isLoading={loading}
                                        isDisabled={loading}
                                        type='submit'
                                    >
                                        Save
                                    </ZaraButton>
                                    <ZaraButton variant='secondary' w='100%' onClick={() => router(PATHS.PROFILE_INFO)}>
                                        Back
                                    </ZaraButton>
                                </Box>
                            </Stack>
                        </Box>
                    </Box>
                </AdaptiveContainer>
            </MenuContainer>
        </Dashboard>
    );
};

export default MobileChangeInfo;
